import type { Lang, Question } from "../types.ts";
import type { ProgressMap } from "../state/store.ts";
import { t } from "../i18n/ui.ts";
import { isFit } from "../sync/types.ts";

export interface Tally {
  /** Right on the last attempt and never got it wrong. */
  correct: number;
  /** Right on the last attempt but wrong at some point before. */
  shaky: number;
  wrong: number;
  unseen: number;
  total: number;
}

/** Buckets questions by how securely they are known. */
export function tally(questions: Question[], progress: ProgressMap): Tally {
  const out: Tally = { correct: 0, shaky: 0, wrong: 0, unseen: 0, total: questions.length };
  for (const q of questions) {
    const stats = progress[String(q.id)];
    if (!stats || stats.lastCorrect === null) out.unseen++;
    // "Fit" is upstream's rule — last two attempts correct — and it is also
    // what drops a question out of the "30 new questions" pool, so the ring
    // and that block always agree about what is done.
    else if (isFit(stats)) out.correct++;
    else if (!stats.lastCorrect) out.wrong++;
    else out.shaky++;
  }
  return out;
}

interface Props {
  lang: Lang;
  /** Outer to inner. Typically: everything, basic knowledge, class-specific. */
  rings: { label: string; tally: Tally }[];
}

const RING_COLORS = {
  correct: "var(--fit-green)",
  shaky: "var(--fit-amber)",
  wrong: "var(--fit-red)",
  unseen: "var(--fit-grey)",
};

/**
 * The "fit for the test" dial: concentric arcs, one per question group, each
 * split into correct / wrong / not-yet-seen. Percentage in the middle is the
 * share of the whole catalogue answered correctly on the last attempt.
 */
export function FitRing({ lang, rings }: Props) {
  const overall =
    rings[0]?.tally ?? { correct: 0, shaky: 0, wrong: 0, unseen: 0, total: 0 };
  // "Fit" counts anything answered right last time, secure or shaky.
  const percent = overall.total
    ? ((overall.correct + overall.shaky) / overall.total) * 100
    : 0;

  const size = 320;
  const center = size / 2;
  const outer = 150;
  const thickness = 26;
  const gap = 8;

  return (
    <div className="fitring">
      <div className="fitring__dial">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="fitring__svg"
        role="img"
        aria-label={t(lang, "statsRingLabel", {
          percent: percent.toFixed(1),
          correct: overall.correct + overall.shaky,
          wrong: overall.wrong,
          unseen: overall.unseen,
          total: overall.total,
        })}
      >
        {rings.map((ring, i) => {
          const radius = outer - i * (thickness + gap) - thickness / 2;
          if (radius < thickness) return null;
          return (
            <Arc
              key={ring.label}
              cx={center}
              cy={center}
              r={radius}
              width={thickness}
              tally={ring.tally}
            />
          );
        })}
      </svg>

        <div className="fitring__center">
          <strong className="fitring__percent">{percent.toFixed(1)}%</strong>
          <span className="fitring__caption">{t(lang, "fitForTest")}</span>
        </div>
      </div>

      <ul className="fitring__legend">
        <li><span className="swatch swatch--correct" /> {t(lang, "statsCorrect")} {overall.correct + overall.shaky}</li>
        <li><span className="swatch swatch--wrong" /> {t(lang, "statsWrong")} {overall.wrong}</li>
        <li><span className="swatch swatch--unseen" /> {t(lang, "statsUnseen")} {overall.unseen}</li>
      </ul>
    </div>
  );
}

/** One concentric ring, drawn as stacked stroke-dasharray segments. */
function Arc({
  cx, cy, r, width, tally,
}: {
  cx: number; cy: number; r: number; width: number; tally: Tally;
}) {
  const circumference = 2 * Math.PI * r;
  const total = Math.max(tally.total, 1);
  const segments: { key: keyof typeof RING_COLORS; value: number }[] = [
    { key: "correct", value: tally.correct },
    { key: "shaky", value: tally.shaky },
    { key: "wrong", value: tally.wrong },
    { key: "unseen", value: tally.unseen },
  ];

  let offset = 0;
  return (
    <g transform={`rotate(-90 ${cx} ${cy})`}>
      {segments.map(({ key, value }) => {
        const length = (value / total) * circumference;
        const dash = `${length} ${circumference - length}`;
        const el = (
          <circle
            key={key}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={RING_COLORS[key]}
            strokeWidth={width}
            strokeDasharray={dash}
            strokeDashoffset={-offset}
          />
        );
        offset += length;
        return el;
      })}
    </g>
  );
}
